import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Swords,
  Shield,
  Crown,
  TrendingUp,
  TrendingDown,
  Target,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  Play,
  Settings,
  History,
  Brain,
  Zap,
  BarChart3,
  DollarSign,
  Percent,
  RefreshCw,
  ChevronRight,
  Award,
  Flame,
  Search,
  Scale,
  Briefcase
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { useLoadingProgress } from "@/components/LoadingIndicators";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Probability meter component
const ProbabilityMeter = ({ value, label }) => {
  const getColor = (v) => {
    if (v >= 70) return "bg-green-500";
    if (v >= 50) return "bg-amber-500";
    return "bg-red-500";
  };
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-bold">{value}%</span>
      </div>
      <div className="h-3 bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColor(value)} transition-all duration-500`}
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
};

// Agent card component with loading progress
const AgentCard = ({ agent, icon: Icon, color, data, loading }) => {
  const progress = useLoadingProgress(loading, 20000);
  
  const borderColor = {
    scout: "border-l-green-500",
    skeptic: "border-l-red-500",
    orchestrator: "border-l-amber-500"
  };
  
  const loadingMessages = {
    scout: "Scanning for high-alpha opportunities...",
    skeptic: "Analyzing risks and value traps...",
    orchestrator: "Making final decision..."
  };
  
  const agentIcons = {
    scout: Search,
    skeptic: Shield,
    orchestrator: Scale
  };
  
  const AgentIcon = agentIcons[agent] || Icon;
  
  return (
    <Card className={`border-l-4 ${borderColor[agent]}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className={`w-4 h-4 text-${color}-500`} />
          {agent === 'scout' ? 'The Scout' : agent === 'skeptic' ? 'The Skeptic' : 'The Orchestrator'}
          {loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
        </CardTitle>
        <CardDescription className="text-xs">
          {agent === 'scout' ? 'Opportunity Hunter' : agent === 'skeptic' ? 'Risk Guardian' : 'Decision Maker'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <AgentIcon className={`w-5 h-5 text-${color}-500 animate-pulse`} />
              <span className="text-sm text-muted-foreground">{loadingMessages[agent]}</span>
            </div>
            <div className="space-y-1">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-right text-muted-foreground">{Math.round(progress)}%</p>
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-muted rounded animate-pulse" style={{ width: '80%' }} />
              <div className="h-3 bg-muted rounded animate-pulse" style={{ width: '60%' }} />
            </div>
          </div>
        ) : data ? (
          <div className="space-y-3 text-sm">
            {agent === 'scout' && (
              <>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="bg-green-500/10">
                    Top Pick: {data.top_pick}
                  </Badge>
                  <span className="text-muted-foreground">
                    Conviction: {data.conviction}%
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {data.sector_focus}
                </p>
              </>
            )}
            {agent === 'skeptic' && (
              <>
                <div className="flex items-center gap-2">
                  <Badge variant={data.approval ? "default" : "destructive"}>
                    {data.approval ? "APPROVED" : "REJECTED"}
                  </Badge>
                  <Badge variant="outline">
                    Risk: {data.risk_assessment}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Max position: {data.max_position_size}
                </p>
              </>
            )}
            {agent === 'orchestrator' && (
              <>
                <div className="flex items-center gap-2">
                  <Badge className={
                    data.decision === 'EXECUTE' ? 'bg-green-500' :
                    data.decision === 'HOLD' ? 'bg-amber-500' : 'bg-red-500'
                  }>
                    {data.decision}
                  </Badge>
                  <span className="text-xs">
                    Expected α: +{data.expected_alpha}%
                  </span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {data.rationale}
                </p>
              </>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Run debate to see analysis</p>
        )}
      </CardContent>
    </Card>
  );
};

export const AutonomousManager = () => {
  const [loading, setLoading] = useState(true);
  const [debating, setDebating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [settings, setSettings] = useState(null);
  const [debate, setDebate] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [memoryVault, setMemoryVault] = useState([]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [aggression, setAggression] = useState(50); // 0=medium, 50=high, 100=maximum

  const fetchData = useCallback(async () => {
    try {
      const [portfolioRes, settingsRes, performanceRes, memoryRes] = await Promise.all([
        axios.get(`${API}/autonomous/portfolio`),
        axios.get(`${API}/autonomous/settings`),
        axios.get(`${API}/autonomous/performance`),
        axios.get(`${API}/autonomous/memory-vault?limit=10`)
      ]);
      
      setPortfolio(portfolioRes.data);
      setSettings(settingsRes.data);
      setPerformance(performanceRes.data);
      setMemoryVault(memoryRes.data.entries || []);
      
      // Set aggression slider
      const level = settingsRes.data.aggression_level;
      setAggression(level === 'maximum' ? 100 : level === 'high' ? 50 : 0);
    } catch (error) {
      console.error("Error fetching autonomous data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const runDebate = async () => {
    setDebating(true);
    try {
      const response = await axios.get(`${API}/autonomous/debate`);
      setDebate(response.data);
      toast.success("Agent debate completed!");
    } catch (error) {
      toast.error("Failed to run debate");
      console.error(error);
    } finally {
      setDebating(false);
    }
  };

  const executeTrade = async (trade) => {
    setExecuting(true);
    try {
      await axios.post(`${API}/autonomous/execute`, trade);
      toast.success(`Executed ${trade.action} for ${trade.symbol}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to execute trade");
    } finally {
      setExecuting(false);
    }
  };

  const updateAggression = async (value) => {
    const level = value >= 75 ? 'maximum' : value >= 25 ? 'high' : 'medium';
    setAggression(value);
    try {
      await axios.put(`${API}/autonomous/settings`, {
        ...settings,
        aggression_level: level
      });
      setSettings({ ...settings, aggression_level: level });
    } catch (error) {
      console.error("Failed to update aggression:", error);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="autonomous-loading">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
      </div>
    );
  }

  const aggressionLabel = aggression >= 75 ? 'MAXIMUM' : aggression >= 25 ? 'HIGH' : 'MEDIUM';
  const aggressionColor = aggression >= 75 ? 'text-red-500' : aggression >= 25 ? 'text-amber-500' : 'text-blue-500';

  return (
    <div className="p-6 lg:p-8 space-y-6 animate-slide-up" data-testid="autonomous-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Crown className="w-8 h-8 text-amber-500" />
            War Room
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Autonomous AI Portfolio Manager • No ROI Ceiling • Maximum Alpha
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={runDebate}
            disabled={debating}
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600"
            data-testid="run-debate-btn"
          >
            {debating ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Swords className="w-5 h-5 mr-2" />}
            Run Agent Debate
          </Button>
        </div>
      </div>

      {/* Portfolio Overview */}
      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-amber-500/30">
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Portfolio Value</p>
              <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {formatCurrency(portfolio?.total_value || 10000)}
              </p>
              <p className="text-xs text-muted-foreground">Starting: $10,000</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total P&L</p>
              <p className={`text-2xl font-bold ${(performance?.total_pnl || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {(performance?.total_pnl || 0) >= 0 ? '+' : ''}{formatCurrency(performance?.total_pnl || 0)}
              </p>
              <p className={`text-sm ${(performance?.total_return_pct || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {(performance?.total_return_pct || 0) >= 0 ? '+' : ''}{(performance?.total_return_pct || 0).toFixed(2)}%
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Alpha vs S&P 500</p>
              <p className={`text-2xl font-bold ${(performance?.alpha_vs_sp500 || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {(performance?.alpha_vs_sp500 || 0) >= 0 ? '+' : ''}{(performance?.alpha_vs_sp500 || 0).toFixed(2)}%
              </p>
              <p className="text-xs text-muted-foreground">S&P: {(performance?.sp500_return_pct || 0).toFixed(2)}%</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Win Rate</p>
              <p className="text-2xl font-bold text-white">{(performance?.win_rate || 0).toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">{performance?.trades_count || 0} trades</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Cash Position</p>
              <p className="text-2xl font-bold text-white">{(performance?.cash_pct || 100).toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">{formatCurrency(portfolio?.cash_balance || 10000)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Aggression Slider */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Flame className={`w-6 h-6 ${aggressionColor}`} />
              <div>
                <p className="font-medium">Aggression Level</p>
                <p className="text-xs text-muted-foreground">Control risk/reward balance</p>
              </div>
            </div>
            <Badge className={`${aggression >= 75 ? 'bg-red-500' : aggression >= 25 ? 'bg-amber-500' : 'bg-blue-500'}`}>
              {aggressionLabel}
            </Badge>
          </div>
          <Slider
            value={[aggression]}
            onValueChange={(v) => updateAggression(v[0])}
            max={100}
            step={1}
            className="w-full"
            data-testid="aggression-slider"
          />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>Medium (Conservative)</span>
            <span>High (Balanced)</span>
            <span>Maximum (Aggressive)</span>
          </div>
        </CardContent>
      </Card>

      {/* Agent Debate Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <AgentCard 
          agent="scout" 
          icon={TrendingUp} 
          color="green"
          data={debate?.scout}
          loading={debating}
        />
        <AgentCard 
          agent="skeptic" 
          icon={Shield} 
          color="red"
          data={debate?.skeptic}
          loading={debating}
        />
        <AgentCard 
          agent="orchestrator" 
          icon={Crown} 
          color="amber"
          data={debate?.orchestrator}
          loading={debating}
        />
      </div>

      {/* Probability of Outperformance */}
      {debate && (
        <Card className="border-2 border-amber-500/30" data-testid="outperformance-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Fund Strategy Analysis
            </CardTitle>
            <CardDescription>
              {debate.investment_type || 'HL Funds & Unit Trusts'} • Min {debate.min_investment || '£100'} per fund
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ProbabilityMeter 
              value={debate.probability_outperform_sp500 || 50} 
              label="Confidence in Beating Cash Savings"
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Expected Return</p>
                <p className="text-lg font-bold text-green-500">{debate.orchestrator?.expected_return || '6-10%'}</p>
              </div>
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Market Regime</p>
                <p className="text-xl font-bold">{debate.market_regime}</p>
              </div>
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Strategy</p>
                <p className="text-xl font-bold">{debate.orchestrator?.regime_strategy || 'N/A'}</p>
              </div>
              <div className="text-center p-3 bg-muted/20 rounded-lg">
                <p className="text-xs text-muted-foreground">Total Cost (OCF)</p>
                <p className="text-xl font-bold">{debate.orchestrator?.total_cost_ocf || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fund Basket Recommendations */}
      {debate?.orchestrator?.fund_basket?.length > 0 && (
        <Card className="border-2 border-green-500/30" data-testid="fund-basket-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-green-500" />
              Recommended Fund Basket
            </CardTitle>
            <CardDescription>
              Buy these funds on Hargreaves Lansdown • Total: £{debate.orchestrator.total_investment || 1000}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {debate.orchestrator.fund_basket.map((fund, idx) => (
                <div key={idx} className="p-4 bg-muted/20 rounded-lg border border-green-500/20">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-green-500">BUY</Badge>
                        <span className="font-bold">{fund.fund}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {fund.thesis || 'Quality fund for diversification'}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          £{fund.amount_gbp || Math.round((debate.orchestrator.total_investment || 1000) * (fund.allocation_pct || 20) / 100)}
                        </span>
                        <span>{fund.allocation_pct || 20}% of basket</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-500">
                        £{fund.amount_gbp || Math.round((debate.orchestrator.total_investment || 1000) * (fund.allocation_pct || 20) / 100)}
                      </p>
                    </div>
                  </div>
                  {fund.hl_action && (
                    <div className="mt-3 p-2 bg-blue-500/10 rounded text-xs text-blue-400 border border-blue-500/20">
                      <span className="font-medium">How to buy: </span>{fund.hl_action}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Summary */}
              <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-400">Total Investment</p>
                    <p className="text-sm text-muted-foreground">{debate.orchestrator.fund_basket.length} funds</p>
                  </div>
                  <p className="text-2xl font-bold text-green-500">
                    £{debate.orchestrator.total_investment || 1000}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Legacy Approved Trades (for backward compatibility) */}
      {debate?.orchestrator?.approved_trades?.length > 0 && !debate?.orchestrator?.fund_basket && (
        <Card data-testid="approved-trades-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Approved Trades
            </CardTitle>
            <CardDescription>Execute these to capture alpha</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {debate.orchestrator.approved_trades.map((trade, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                  <div className="flex items-center gap-4">
                    <Badge className={trade.action === 'BUY' ? 'bg-green-500' : 'bg-red-500'}>
                      {trade.action}
                    </Badge>
                    <div>
                      <p className="font-bold">{trade.symbol}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{trade.thesis}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium">{trade.allocation_pct}% allocation</p>
                      <p className="text-xs text-muted-foreground">
                        ~{formatCurrency((portfolio?.cash_balance || 10000) * trade.allocation_pct / 100)}
                      </p>
                    </div>
                    <Button 
                      size="sm"
                      onClick={() => executeTrade(trade)}
                      disabled={executing}
                      data-testid={`execute-${trade.symbol}-btn`}
                    >
                      {executing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Holdings */}
      {portfolio?.holdings?.length > 0 && (
        <Card data-testid="holdings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Current Holdings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {portfolio.holdings.map((holding, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
                  <div>
                    <p className="font-bold">{holding.symbol}</p>
                    <p className="text-xs text-muted-foreground">{holding.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{formatCurrency(holding.market_value)}</p>
                    <p className={`text-sm ${holding.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {holding.pnl >= 0 ? '+' : ''}{formatCurrency(holding.pnl)} ({holding.pnl_pct?.toFixed(1)}%)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Memory Vault */}
      <Card data-testid="memory-vault-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Memory Vault
          </CardTitle>
          <CardDescription>Trade thesis history • AI learns from past decisions</CardDescription>
        </CardHeader>
        <CardContent>
          {memoryVault.length > 0 ? (
            <div className="space-y-3">
              {memoryVault.map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-muted/10 rounded-lg border border-muted">
                  <div className="flex items-center gap-3">
                    <Badge variant={entry.action === 'BUY' ? 'default' : 'destructive'}>
                      {entry.action}
                    </Badge>
                    <div>
                      <p className="font-medium">{entry.symbol}</p>
                      <p className="text-xs text-muted-foreground line-clamp-1">{entry.thesis}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {entry.thesis_correct === true && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {entry.thesis_correct === false && <XCircle className="w-4 h-4 text-red-500" />}
                    {entry.thesis_correct === null && <Badge variant="outline">Pending</Badge>}
                    <span className="text-xs text-muted-foreground">
                      {new Date(entry.trade_date).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No trades yet. Run a debate and execute trades to build the memory vault.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="text-center py-4 border-t border-border">
        <p className="text-xs text-muted-foreground">
          Autonomous Manager uses AI agents (Scout, Skeptic, Orchestrator) to maximize alpha.
          No artificial ROI ceiling. Paper trading mode.
        </p>
      </div>
    </div>
  );
};

export default AutonomousManager;
